import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { useUserRolePermissions } from 'hooks';
import * as R from 'ramda';
import { AuthContext } from 'authentication/AuthContext';
import DialogExplicitConfirmation from 'common/dialog-explicit-confirmation/DialogExplicitConfirmation';
import { dialogContainerStyles } from 'common/dialog-explicit-confirmation/DialogExplicitConfirmation.styles';
import { MDialog, StatusDetailsDialog } from 'common';
import { createSnack } from 'snackbar/snackbarSlice';
import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import PropTypes from 'prop-types';
import { GT_ZERO, INTEGER, REQUIRED } from 'utils/helpers/validationMessages';
import { isNumberWithOptionalFormatting } from 'utils/helpers/formValidations';
import Tooltip from '@mui/material/Tooltip';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';
import palette from 'library/theme/palette';
import {
  useAcceptShipmentMutation,
  useBulkAcceptShipmentMutation,
  useCancelShipmentMutation,
  useCreateDistributionsMutation,
  useEditDistributionsMutation,
  useEditShipmentMutation,
  useGetBookingOriginsQuery,
  useGetEditShipmentQuery,
  useGetShipmentDistributionsQuery,
  useRejectShipmentMutation,
  useViewBatchQuery,
  useViewShipmentQuery,
} from '../batchesEndpoints';
import DataDisplayItem from '../components/DataDisplayItem';
import NoAvailableCard from '../components/NoAvailableCard';
import EditShipment from '../edit/EditShipment';
import { formatDate, isEditableStatus, shipmentStatusTooltips } from '../utils';
import DistributionsDrawer from '../DistributionsDrawer/DistributionsDrawer';
import ViewShipmentHeader from './ViewShipmentHeader';
import ViewAssociatedDistributions from './ViewAssociatedDistributions';
import { baseDistribution } from '../distributions/create/initialState';
import StatusChip from '../components/StatusChip';

const containerStyle = {
  margin: '0 auto',
  maxWidth: '1470px',
  pb: 6,
  pt: 6,
  px: { lg: 3, xs: 2 },
  width: '100%',
};
const infoContainerStyle = {
  columnGap: { lg: 3 },
  display: 'grid',
  gridTemplateColumns: { lg: 'repeat(3, 1fr)', xs: '1fr' }, // Add spacing between columns
  marginBottom: 4,
  padding: 0,
  rowGap: { lg: 3, xs: 0 },
  width: '100%',
};

const ViewShipmentById = ({ pageName }) => {
  const dispatch = useDispatch();
  const { isProductShippingManage } = useUserRolePermissions();
  const { shipmentId } = useParams();
  const params = { shipmentId };
  const { affiliates, currentAffiliate } = useContext(AuthContext);
  const navigate = useNavigate();
  const { state } = useLocation();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const {
    data: shipmentData = { shipments: [] },
    isSuccess: isShipmentSuccess,
    isFetching: isFetchingShipment,
    refetch: refetchShipment,
  } = useViewShipmentQuery(
    {
      params,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const shipperName = R.path(['shipper', 'shipperName'], shipmentData);
  const shipperDataId = R.path(['shipper', 'dataCompanyId'], shipmentData);
  const matchedAffiliate = affiliates?.find(
    affiliate => affiliate?.customerNameShort === shipperName
  );
  const shipperDataCompany = matchedAffiliate ? matchedAffiliate?.dataCompanyId : null;

  const { data: shipmentEditData = { shipments: [] } } = useGetEditShipmentQuery({
    params,
  });

  const shipmentDataProp = R.prop(R.__, shipmentData);
  const shipmentEditDataProp = R.prop(R.__, shipmentEditData);
  const initialVolume = shipmentDataProp('volume');

  const [areAllUpdatesValid, setAreAllUpdatesValid] = useState(false);
  const [shipmentUpdates, setShipmentUpdates] = useState({});
  const [isVolumeChangeDialogOpen, setIsVolumeChangeDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const shipmentUpdatesProp = R.prop(R.__, shipmentUpdates);
  const [cancelShipment] = useCancelShipmentMutation();
  const [volumeChangeMessage, setVolumeChangeMessage] = useState('');
  const [isDistributionsVolumeInvalid, setIsDistributionsVolumeInvalid] = useState(false);
  const [selectedDistribution, setSelectedDistribution] = useState({});
  const onEditPage = isProductShippingManage && isEditableStatus(shipmentDataProp('status'));
  const [distributionEdited, setDistributionEdited] = useState(false);
  const [updatedRows, setUpdatedRows] = useState([]);
  const customerIdName = R.pipe(R.defaultTo(''), R.trim, R.split(/\s+/), R.head);
  const [duplicateDistributionId, setDuplicateDistributionId] = useState(null);
  const shipmentBulkConfirmFlag = EvaluateFlag(process.env.REACT_APP_LD_SHIPMENTS_BULK_CONFIRM);

  useEffect(() => {
    if (!isFetchingShipment && isShipmentSuccess) {
      setShipmentUpdates(
        R.mergeLeft({
          agreementCode: shipmentDataProp('agreementCode'),
          bookingOrigin: {
            bookingOriginId: shipmentDataProp('bookingOriginId'),
            bookingOriginName: shipmentDataProp('bookingOriginName'),
          },
          comments: shipmentDataProp('comments') || '',
          locationName: shipmentDataProp('locationName'),
          requestedDate: shipmentDataProp('requestedDate'),
          volume: formatNumberWithoutDecimals(shipmentDataProp('volume') || 0),
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchingShipment, isShipmentSuccess]);

  const [isAcceptDialogOpen, setIsAcceptDialogOpen] = useState(false);
  const [isBackDialogOpen, setIsBackDialogOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [isStatusDetailsDialogOpen, setIsStatusDetailsDialogOpen] = useState(false);

  const [isExplicitDialogOpen, setIsExplicitDialogOpen] = useState(false);

  const [acceptShipment] = useAcceptShipmentMutation();
  const [bulkAcceptShipment] = useBulkAcceptShipmentMutation();
  const [editShipment] = useEditShipmentMutation();
  const [rejectShipment] = useRejectShipmentMutation();

  const handleAcceptShipment = () => {
    const dataCompanyId = shipperDataId || shipperDataCompany;
    const selectedShipmentId = shipmentDataProp('shipmentId');
    const acceptShipmentRequest = shipmentBulkConfirmFlag
      ? bulkAcceptShipment({
          body: {
            dataCompanyId,
            shipmentIds: [selectedShipmentId],
          },
        })
      : acceptShipment({
          body: {
            dataCompanyId,
            shipmentId: selectedShipmentId,
          },
        });

    acceptShipmentRequest.then(res => {
      if (res.error || res.data?.failureCount > 0) {
        dispatch(
          createSnack({
            message:
              res.error?.data?.title || res.data?.title || 'The shipment could not be accepted.',
            options: { variant: 'error' },
          })
        );
      } else {
        refetchShipment();
        dispatch(
          createSnack({
            message: res.data?.message || 'The shipment was successfully accepted.',
          })
        );
      }
    });
    setIsAcceptDialogOpen(false);
  };

  // associated distributions realated data

  const [newDistributionsDataValidated, setNewDistributionsDataValidated] = useState(false);

  const {
    data: shipmentDistributionsData = { distributions: [] },
    isFetching: isFetchingDistributions,
    isSuccess: isDistributionsSuccess,
    refetch: refetchShipmentDistributions,
  } = useGetShipmentDistributionsQuery(
    {
      params: { shipmentId: parseInt(shipmentId) },
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [associatedDistributions, setAssociatedDistributions] = useState(
    R.prop('distributions', shipmentDistributionsData)
  );

  useEffect(() => {
    if (isDistributionsSuccess) {
      setAssociatedDistributions(R.prop('distributions', shipmentDistributionsData));
    }
  }, [isDistributionsSuccess, shipmentDistributionsData]);

  const validateVolume = value =>
    R.cond([
      [R.isEmpty(R.__), R.always(REQUIRED)],
      [R.gt(1), R.always(GT_ZERO)],
      [val => !isNumberWithOptionalFormatting(val), R.always(INTEGER)],
      [R.T, R.always('')],
    ])(value);

  // validation for new distributions
  useEffect(() => {
    const newDistributions = R.filter(row => R.prop('isNew', row))(associatedDistributions);
    if (
      newDistributions.length !== 0 &&
      R.all(
        R.identity,
        newDistributions.map(row => {
          return R.all(R.identity, [
            R.isEmpty(validateVolume(R.prop('volume', row))),
            R.prop('inventoryOwnerName', row),
            R.prop('locationName', row),
          ]);
        })
      )
    ) {
      setNewDistributionsDataValidated(true);
    } else {
      setNewDistributionsDataValidated(false);
    }
  }, [associatedDistributions]);
  useEffect(() => {
    // Reset duplicate error when distributions change
    if (duplicateDistributionId !== null && associatedDistributions.length > 0) {
      // Check if the distribution that was marked as duplicate has been changed
      const distributionChanged = associatedDistributions.some(dist => {
        const isDuplicate = dist.distributionId === duplicateDistributionId;
        const otherDists = associatedDistributions.filter(
          d => d.distributionId !== duplicateDistributionId
        );
        const noDuplicateRemaining = !otherDists.some(
          d =>
            d.inventoryOwnerName === dist.inventoryOwnerName && d.locationName === dist.locationName
        );
        return isDuplicate && noDuplicateRemaining;
      });

      if (distributionChanged) {
        setDuplicateDistributionId(null);
      }
    }
  }, [associatedDistributions, duplicateDistributionId]);

  const handleAddNewDistribution = () => {
    setAssociatedDistributions(
      R.append({
        ...baseDistribution,
        distributionId: associatedDistributions.length + 1,
        isNew: true,
      })
    );

    // scroll to the bottom of the page after datagrid view is updated
    setTimeout(() => {
      window.scrollTo(0, document.body.scrollHeight);
    }, 500);
  };

  const [submit] = useCreateDistributionsMutation();

  const dataCompanyId = R.path(['shipper', 'dataCompanyId'], shipmentData);

  // inventoryOwnerToAffiliate :: InventoryOwner -> Affiliate
  const inventoryOwnerToAffiliate = ({ inventoryOwnerIdName: companyCustomerIdName }) => ({
    companyCustomerIdName,
    dataCompanyId: parseInt(dataCompanyId),
  });

  // recipientToAffiliate :: Recipient -> Affiliate
  const recipientToAffiliate = ({ recipientIdName: companyCustomerIdName }) => ({
    companyCustomerIdName,
    dataCompanyId: parseInt(dataCompanyId),
  });

  const distributionTransformations = {
    chainRecipients: R.map(recipientToAffiliate),
    inventoryOwner: inventoryOwnerToAffiliate,
  };

  const handleSaveNewDistributions = () => {
    setNewDistributionsDataValidated(false);
    setDuplicateDistributionId(null);
    let hasDuplicates = false;
    const newDistributions = associatedDistributions.filter(row => R.prop('isNew', row));
    shipmentDistributionsData.distributions.forEach(row => {
      newDistributions.forEach(newRow => {
        if (
          R.prop('inventoryOwnerName', row) === R.prop('inventoryOwnerName', newRow) &&
          R.prop('locationName', row) === R.prop('locationName', newRow)
        ) {
          if (updatedRows.length > 0 && updatedRows.includes(row.distributionId)) return;
          hasDuplicates = true;
          setDuplicateDistributionId(R.prop('distributionId', row));
          dispatch(
            createSnack({
              message:
                'A distribution already exists with this Inventory Owner and Location combination. Please update the existing distribution',
              options: { variant: 'error' },
            })
          );
        }
      });
    });
    if (hasDuplicates) return Promise.resolve();
    const filteredDistributions = R.map(
      R.pipe(
        R.omit(['isNew']),
        R.pick([
          'chainRecipients',
          'inventoryOwner',
          'locationId',
          'reconsignmentControl',
          'shipmentChain',
          'volume',
          'volumeDisplay',
        ])
      ),
      newDistributions
    );
    const distributionsState = {
      distributions: filteredDistributions,
      shipmentId: R.prop('shipmentId', shipmentData),
    };

    const body = R.evolve(
      {
        distributions: R.map(R.evolve(distributionTransformations)),
        shipmentId: R.compose(parseInt),
      },
      distributionsState
    );

    return submit({ body }).then(res => {
      if (res.error) {
        setUpdatedRows(body.distributions);
      } else {
        refetchShipment();
        setDuplicateDistributionId(null);
        dispatch(
          createSnack({
            message: res.data.message,
          })
        );
      }
    });
  };
  // used for volume change confirmation modal
  const { data: batchData = {}, refetch: refetchBatchData } = useViewBatchQuery(
    {
      params: { batchId: shipmentDataProp('batchId') },
    },
    {
      skip: !shipmentDataProp('batchId'),
    }
  );
  const batchDataProp = R.prop(R.__, batchData);
  const updatedBatchVolume = R.compose(R.add(batchDataProp('volume')), R.subtract);

  const [editDistribution] = useEditDistributionsMutation();

  const handleEditDistributions = async () => {
    const hasDuplicates = false;
    const filteredEditDistributions = R.pipe(
      R.filter(distribution => R.includes(distribution?.distributionId, updatedRows)),
      R.sortBy(R.prop('distributionId'))
    )(associatedDistributions);

    const initialDistributions = R.prop('distributions', shipmentDistributionsData);
    // Sort the filtered distributions based on the volume gap of before and after edit
    // distribution with the smallest edited - initial volume gap will be first
    const sortedDistributions = R.sortWith(
      [
        R.ascend(distribution => {
          const initialDist = R.find(
            R.propEq('distributionId', R.prop('distributionId', distribution)),
            initialDistributions
          );
          return R.subtract(R.prop('volume', distribution), R.prop('volume', initialDist));
        }),
      ],
      filteredEditDistributions
    );
    sortedDistributions.forEach(editedRow => {
      const otherDistributions = associatedDistributions.filter(
        row => row.distributionId !== editedRow.distributionId
      );
      otherDistributions.forEach(row => {
        if (
          R.prop('inventoryOwnerName', editedRow) === R.prop('inventoryOwnerName', row) &&
          R.prop('locationName', editedRow) === R.prop('locationName', row)
        ) {
          setDuplicateDistributionId(R.prop('distributionId', row));
          dispatch(
            createSnack({
              message:
                'A distribution already exists with this Inventory Owner and Location combination. Please update the existing distribution',
              options: { variant: 'error' },
            })
          );
        }
      });
    });
    if (hasDuplicates) {
      refetchShipmentDistributions();
      return;
    }
    await sortedDistributions.reduce(async (promise, distribution) => {
      await promise; // Wait for previous iteration
      const shipmentChainValue = R.prop('shipmentChain', distribution);
      const response = await editDistribution({
        body: {
          chainRecipients:
            shipmentChainValue !== 0
              ? [
                  {
                    companyCustomerIdName: customerIdName(
                      R.prop('inventoryOwnerName', distribution)
                    ),
                    dataCompanyId: R.path(['batchOwner', 'dataCompanyId'], batchData),
                  },
                ]
              : [],
          distributionId: R.prop('distributionId', distribution),
          locationId: R.prop('locationId', distribution),
          reconsignmentControl: R.prop('reconsignmentControl', distribution),
          shipmentChain: shipmentChainValue !== 0,
          volume: R.prop('volume', distribution),
        },
      });
      if (response.error) {
        dispatch(
          createSnack({
            message: response.error.data.title,
            options: { variant: 'error' },
          })
        );
      }
      dispatch(createSnack({ message: response.data.message }));
      refetchShipment();
      setDuplicateDistributionId(null);
    }, Promise.resolve());

    setUpdatedRows([]);
  };
  const handleVolumeReset = () => {
    setIsVolumeChangeDialogOpen(false);
    setShipmentUpdates(R.mergeLeft({ volume: formatNumberWithoutDecimals(initialVolume || 0) }));
  };

  const hasNoUpdates = R.all(R.equals(true), [
    R.equals(
      R.path(['bookingOrigin', 'bookingOriginId'], shipmentUpdates),
      shipmentDataProp('bookingOriginId')
    ),
    R.eqProps('requestedDate', shipmentUpdates, shipmentData),
    R.equals(shipmentUpdates?.volume?.replace(/,/g, ''), shipmentDataProp('volume')?.toString()),
    R.equals(shipmentUpdates?.comments, shipmentDataProp('comments') || ''),
    R.equals(shipmentUpdates?.agreementCode, shipmentDataProp('agreementCode')),
  ]);
  const [isSaving, setIsSaving] = useState(false);
  const handleEditShipment = async () => {
    setIsSaving(true);
    try {
      // First handle shipment updates if needed
      if (!hasNoUpdates) {
        const res = await editShipment({
          body: {
            agreementCode: shipmentUpdatesProp('agreementCode'),
            bookingOriginId: R.path(['bookingOrigin', 'bookingOriginId'], shipmentUpdates),
            comment: shipmentUpdatesProp('comments'),
            requestedDate: shipmentUpdatesProp('requestedDate'),
            shipmentId: shipmentDataProp('shipmentId'),
            volume: parseInt(shipmentUpdatesProp('volume')?.replace(/,/g, '')),
          },
        });

        if (res.error) {
          dispatch(
            createSnack({
              message: res.error.data.title,
              options: { variant: 'error' },
            })
          );
          return; // Exit early if there's an error
        }

        dispatch(
          createSnack({
            message: res.data.message,
          })
        );

        // Wait for refetch to complete before continuing
        await new Promise(resolve => {
          setTimeout(async () => {
            await refetchShipment();
            await refetchBatchData();
            resolve();
          }, 500);
        });
      }

      // Then handle distribution edits if needed
      if (distributionEdited) {
        await handleEditDistributions();
        setDistributionEdited(false);
      }

      // Finally handle new distributions if validated
      if (newDistributionsDataValidated) {
        await handleSaveNewDistributions();
        setNewDistributionsDataValidated(false);
      }

      // Final refetch and navigation after all operations are complete
      await Promise.all([refetchShipment(), refetchBatchData()]);
      navigate(`../batches/shipment/${shipmentId}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error during edit process:', error);
    } finally {
      setIsSaving(false);
      setUpdatedRows([]);
    }
  };

  const handlePopupOpen = () => {
    if (isSaving) return; // Prevent opening if already saving
    if (
      parseInt(shipmentDataProp('volume') || 0) <
      parseInt(shipmentUpdatesProp('volume')?.replace(/,/g, '') || 0)
    ) {
      setVolumeChangeMessage('increase');
    } else {
      setVolumeChangeMessage('decrease');
    }
    if (formatNumberWithoutDecimals(initialVolume || 0) !== shipmentUpdatesProp('volume')) {
      setIsVolumeChangeDialogOpen(true);
    } else {
      handleEditShipment();
    }
  };

  const handleVolumeConfirmChange = () => {
    setIsSaving(true);
    handleEditShipment();
    setIsVolumeChangeDialogOpen(false);
  };

  const handleRejectShipmentPopupOpen = () => {
    setIsRejectDialogOpen(true);
  };

  const handleAcceptShipmentPopupOpen = () => {
    setIsAcceptDialogOpen(true);
  };
  const handleCancelShipmentPopupOpen = () => {
    setDialogType('shipment');
    setIsExplicitDialogOpen(true);
  };
  const hasStatusDetails = R.pipe(
    R.propOr([], 'distributions'),
    R.pluck('statusDetails'),
    R.any(s => !R.isNil(s) && String(s).trim() !== '')
  )(shipmentDistributionsData);

  const handleRejectShipment = () => {
    rejectShipment({
      body: { shipmentId: shipmentDataProp('shipmentId') },
    }).then(res => {
      setIsRejectDialogOpen(false);
      if (res.error) {
        dispatch(
          createSnack({
            message: res.error.data.title,
            option: { variant: 'error' },
          })
        );
      } else {
        refetchShipment();
        dispatch(
          createSnack({
            message: res.data.message,
          })
        );
      }
    });
  };

  const cancelSelectedShipment = () => {
    cancelShipment({ body: { shipmentId } })
      .then(res => {
        setIsExplicitDialogOpen(false);
        if (res.error) {
          dispatch(
            createSnack({
              message: res.error.data.title,
              options: { variant: 'error' },
            })
          );
        } else {
          refetchShipment();
          dispatch(
            createSnack({
              message: res.data.message,
            })
          );
        }
      })
      .finally(() =>
        setTimeout(() => {
          setDialogType('');
        }, 500)
      );
  };

  const handleConfirm = () => {
    if (R.equals('shipment', dialogType)) cancelSelectedShipment();
  };

  const warningList = R.cond([
    [
      R.equals('shipment'),
      R.always([
        'This will also cancel all associated distributions.',
        'This action cannot be undone.',
      ]),
    ],
    [R.T, R.always([])],
  ])(dialogType);

  const isBatchOwner = affiliates.some(affiliate =>
    R.propEq('customerNameShort', shipmentDataProp('batchOwnerName'), affiliate)
  );

  const hasAffiliateShipper = affiliates.some(affiliate =>
    R.propEq('customerNameShort', R.path(['shipper', 'shipperName'], shipmentData), affiliate)
  );

  const hasAffliateDataCompany = () => {
    const shipmentAffiliate = affiliates?.find(
      affiliate => affiliate?.customerNameShort === shipperName
    );
    return shipmentAffiliate
      ? shipmentAffiliate.dataCompanyId === currentAffiliate?.dataCompanyId
      : false;
  };

  const shipmentStatus = shipmentDataProp('status');

  const hasCancelPermissions = R.all(R.equals(true), [
    isBatchOwner,
    isProductShippingManage,
    isEditableStatus(shipmentDataProp('status')),
  ]);

  const hasAcceptPermissions = R.all(R.equals(true), [
    hasAffliateDataCompany(),
    hasAffiliateShipper,
    isProductShippingManage,
    R.equals('Unconfirmed', shipmentDataProp('status')),
  ]);

  const hasEditPermissions = R.all(R.equals(true), [
    isBatchOwner,
    isProductShippingManage,
    isEditableStatus(shipmentDataProp('status')),
  ]);

  const hasRejectPermissions = R.all(R.equals(true), [
    hasAffiliateShipper,
    isProductShippingManage,
    isEditableStatus(shipmentDataProp('status')),
  ]);

  const hasIsRejectShipmentsDifferentBatchOwner = R.all(R.equals(true), [
    R.and(isProductShippingManage, hasAffiliateShipper),
    !R.equals(R.path(['shipper', 'shipperName'], shipmentData), shipmentDataProp('batchOwnerName')),
    !R.equals(currentAffiliate?.customerNameShort, shipmentDataProp('batchOwnerName')),
    !R.equals(
      currentAffiliate?.customerNameShort,
      R.path(['shipper', 'shipperName'], shipmentData)
    ),
    R.equals(matchedAffiliate?.dataCompanyId, currentAffiliate?.dataCompanyId),
  ]);

  const handleEdit = () => {
    navigate('./edit', {
      state: {
        from: state?.from,
        url: state?.from === 'Parent Batch' ? -2 : '../batches/shipment',
      },
    });
  };

  const { data: bookingOrigins = [], isFetching: isFetchingBookingOrigins } =
    useGetBookingOriginsQuery(
      {
        params: {
          inventoryOwner: {
            companyCustomerIdName: customerIdName(R.path(['shipper', 'shipperName'], shipmentData)),
            dataCompanyId: R.path(['shipper', 'dataCompanyId'], shipmentData),
          },
          originId: shipmentDataProp('originId'),
        },
      },
      { skip: !onEditPage || isFetchingShipment || R.isNil(currentAffiliate) }
    );

  const newBatchId = shipmentDataProp('batchId');
  const hasParentBatch = !R.isNil(newBatchId) && !R.isEmpty(newBatchId);

  const handleBatchIdNavigation = () => {
    navigate(pageName ? -1 : `../batches/batch/${newBatchId}/edit`);
  };

  const handleShipmentsNavigation = () => {
    navigate('../batches/shipment');
  };

  const buttonProps = {
    fullWidth: isMobile,
    sx: { borderColor: 'red', color: 'red', mt: '10px' },
    variant: 'outlined',
  };

  return (
    <Box alignItems="top" display="flex" justifyContent="center">
      <Box sx={containerStyle}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: '1.5rem', mt: '1.5rem' }}>
          <Typography
            color="inherit"
            onClick={hasParentBatch ? handleBatchIdNavigation : handleShipmentsNavigation}
            sx={{
              '&:hover': {
                color: 'green',
                cursor: 'pointer',
              },
            }}
            variant="caption"
          >
            {hasParentBatch ? `Batch #${newBatchId}` : 'Shipments'}
          </Typography>
          <Typography color="secondary.main" sx={{ pr: 8 }} variant="caption">
            Shipment #{shipmentId}
          </Typography>
        </Breadcrumbs>
        {!onEditPage && (
          <ViewShipmentHeader
            fromPage={state}
            onAcceptClick={() => setIsAcceptDialogOpen(true)}
            onRejectClick={() => setIsRejectDialogOpen(true)}
            showAcceptButton={hasAcceptPermissions && !onEditPage}
            showRejectButton={hasRejectPermissions && !onEditPage}
          />
        )}
        <Stack
          alignItems="center"
          direction="row"
          justifyContent={{ sm: 'flex-start', xs: 'space-between' }}
          sx={{ pr: 1 }}
        >
          <Typography sx={{ mb: '1.5rem', mr: 1 }} variant="h5">
            Shipment Information
          </Typography>
          {hasEditPermissions && !onEditPage && (
            <IconButton onClick={handleEdit}>
              <EditIcon fontSize="small" sx={{ color: 'secondary.main' }} />
            </IconButton>
          )}
        </Stack>
        {isFetchingShipment ? (
          <Box textAlign="center" width="100%">
            <CircularProgress size={48} />
          </Box>
        ) : !shipmentDataProp('shipmentId') ? (
          <NoAvailableCard />
        ) : (
          <>
            <Box sx={infoContainerStyle}>
              <Stack direction="column" spacing={4}>
                <DataDisplayItem label="Shipment ID" value={`#${shipmentDataProp('shipmentId')}`} />
                <DataDisplayItem
                  label="Shipper"
                  value={R.path(['shipper', 'shipperName'], shipmentData)}
                />
                <DataDisplayItem label="Batch Owner" value={shipmentDataProp('batchOwnerName')} />
                <DataDisplayItem label="Product" value={shipmentDataProp('productName')} />
                <Stack spacing={0.5}>
                  <Typography variant="caption">Status</Typography>
                  <Tooltip placement="top" title={shipmentStatusTooltips[shipmentStatus]}>
                    <StatusChip status={shipmentStatus} />
                  </Tooltip>
                </Stack>
                <br />
                <DataDisplayItem
                  label="Status Details"
                  value={shipmentEditDataProp('statusDetails')}
                />
                {hasStatusDetails && hasAffiliateShipper && (
                  <Button
                    fullWidth={false}
                    onClick={() => setIsStatusDetailsDialogOpen(true)}
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      justifyContent: 'center',
                      justifySelf: 'flex-start',
                      lineHeight: '1',
                      padding: '12px16px !important',
                      width: 'fit-content !important',
                    }}
                    variant="outlined"
                  >
                    View Distribution Status Details
                  </Button>
                )}
              </Stack>
              <Stack direction="column" spacing={4}>
                <DataDisplayItem
                  label={
                    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                      Default Auto-Distribution Location
                      <Tooltip
                        arrow
                        enterTouchDelay={10}
                        placement="top"
                        title={
                          <span
                            style={{
                              display: 'block',
                              fontSize: '13px',
                              padding: '3px',
                              wordWrap: 'break-word',
                            }}
                          >
                            If you do not create a distribution for this shipment, all of the
                            barrels will auto-distribute to this defined location.
                          </span>
                        }
                      >
                        <InfoRoundedIcon fontSize="small" sx={{ color: 'graphic.main' }} />
                      </Tooltip>
                    </Box>
                  }
                  value={shipmentUpdatesProp('locationName') || shipmentDataProp('locationName')}
                />

                <DataDisplayItem label="Booked Volume" value={shipmentDataProp('bookedVolume')} />
                <DataDisplayItem
                  label="Creation Date"
                  value={
                    shipmentDataProp('creationDate') && formatDate(shipmentDataProp('creationDate'))
                  }
                />
                <DataDisplayItem
                  label="Available Date"
                  value={
                    shipmentDataProp('availableDate') &&
                    formatDate(shipmentDataProp('availableDate'))
                  }
                />
              </Stack>
              <Stack direction="column" spacing={4}>
                {R.prop('requestedDate', shipmentUpdates) &&
                  R.path(['bookingOrigin', 'bookingOriginId'], shipmentUpdates) && (
                    <EditShipment
                      availableDate={shipmentDataProp('availableDate')}
                      batchOwnerName={shipmentDataProp('batchOwnerName')}
                      bookingOriginOptions={bookingOrigins}
                      hasAcceptPermissions={hasAcceptPermissions}
                      isDistributionsVolumeInvalid={isDistributionsVolumeInvalid}
                      isFetchingBookingOrigins={isFetchingBookingOrigins}
                      onEditPage={onEditPage}
                      origin={shipmentDataProp('originName')}
                      setAreAllUpdatesValid={setAreAllUpdatesValid}
                      setShipmentUpdates={setShipmentUpdates}
                      shipmentUpdates={shipmentUpdates}
                      shipperName={R.path(['shipper', 'shipperName'], shipmentData)}
                      status={shipmentDataProp('status')}
                      totalDistributedVolume={shipmentDataProp('totalDistributedVolume')}
                    />
                  )}
              </Stack>
            </Box>
            {onEditPage && (
              <MDialog
                header="Unsaved Changes"
                leftButtonAction={() => {
                  setIsBackDialogOpen(false);
                  navigate('../batches/shipment');
                }}
                leftButtonText="Continue"
                onClose={() => {
                  setIsBackDialogOpen(false);
                }}
                open={isBackDialogOpen}
                rightButtonAction={() => setIsBackDialogOpen(false)}
                rightButtonText="Keep Editing"
                slotProps={{ paper: { sx: dialogContainerStyles } }}
              >
                <Typography>
                  Are you sure you want to leave this page? You have unsaved changes.
                </Typography>
              </MDialog>
            )}
            <StatusDetailsDialog
              detailsKey="statusDetails"
              idKey="distributionId"
              items={R.propOr([], 'distributions', shipmentDistributionsData)}
              nameKey="inventoryOwnerName"
              onClose={() => setIsStatusDetailsDialogOpen(false)}
              open={isStatusDetailsDialogOpen}
              sx={{
                '& .MuiStack-root > .MuiBox-root .MuiTypography-root': {
                  color: `${palette.graphic.dark} !important`,
                },
              }}
              title="Distribution Status Details"
            />
          </>
        )}
        {hasAffiliateShipper && hasAffliateDataCompany() && (
          <>
            <Divider sx={{ mb: { lg: 4, xs: 3 }, mt: { lg: 4, xs: 3 } }} />
            <ViewAssociatedDistributions
              associatedDistributions={associatedDistributions}
              availableDate={shipmentDataProp('availableDate')}
              duplicateDistributionId={duplicateDistributionId}
              isFetchingDistributions={isFetchingDistributions}
              isFetchingShipment={isFetchingShipment}
              isProductShippingManage
              isShipper={hasAffiliateShipper}
              onEditPage={onEditPage}
              refetchShipment={refetchShipment}
              refetchShipmentDistributions={refetchShipmentDistributions}
              refreshBatchData={refetchBatchData}
              setAssociatedDistributions={setAssociatedDistributions}
              setDistributionEdited={setDistributionEdited}
              setIsDistributionsVolumeInvalid={setIsDistributionsVolumeInvalid}
              setSelectedDistribution={setSelectedDistribution}
              setUpdatedRows={setUpdatedRows}
              shipmentData={shipmentData}
              shipmentUpdates={shipmentUpdates}
              totalResults={R.prop('totalResults', shipmentDistributionsData) ?? 0}
              updatedRows={updatedRows}
            />
            <DistributionsDrawer
              onClose={setSelectedDistribution}
              setSelectedDistribution={setSelectedDistribution}
              {...selectedDistribution}
            />
          </>
        )}
        {onEditPage && (
          <DialogActions
            sx={{
              backgroundColor: '#FFFFFF',
              bottom: 0,
              boxShadow: 0,
              display: 'flex',
              flexDirection: 'row',
              height: '80px',
              justifyContent: 'space-between',
              left: 0,
              p: 3,
              position: 'fixed',
              right: 0,
              width: '100%',
              zIndex: 200,
            }}
          >
            {!hasAcceptPermissions && (
              <Stack direction="row" justifyContent="flex-start" spacing={1} sx={{ mb: 1, mt: 1 }}>
                {hasCancelPermissions && !hasIsRejectShipmentsDifferentBatchOwner ? (
                  <Button {...buttonProps} onClick={handleCancelShipmentPopupOpen}>
                    Cancel Shipment
                  </Button>
                ) : (
                  <Button {...buttonProps} onClick={handleRejectShipmentPopupOpen}>
                    Reject Shipment
                  </Button>
                )}
              </Stack>
            )}
            <Stack direction="row" justifyContent="flex-start" spacing={1} sx={{ mb: 1, mt: 1 }}>
              {hasAcceptPermissions && (
                <Button {...buttonProps} onClick={handleRejectShipmentPopupOpen}>
                  Reject Shipment
                </Button>
              )}
            </Stack>
            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: 1, mt: 1 }}>
              {hasAcceptPermissions ? (
                <Button
                  fullWidth={isMobile}
                  onClick={() => {
                    handleAcceptShipmentPopupOpen();
                  }}
                  sx={{ borderColor: 'green', color: 'green', mt: '10px' }}
                  variant="outlined"
                >
                  Accept Shipment
                </Button>
              ) : (
                <>
                  {R.and(isProductShippingManage, hasAffiliateShipper) &&
                    hasAffliateDataCompany() && (
                      <Button
                        fullWidth={isMobile}
                        onClick={handleAddNewDistribution}
                        sx={{ mt: '10px' }}
                        variant="outlined"
                      >
                        New Distribution
                      </Button>
                    )}
                  <Button
                    // enabled if
                    // 1. has update and all updates are valid
                    // 2. all updates are valid and new distributions are validated
                    disabled={
                      !(
                        (!hasNoUpdates && areAllUpdatesValid) ||
                        (areAllUpdatesValid && newDistributionsDataValidated) ||
                        (areAllUpdatesValid && distributionEdited)
                      ) ||
                      isDistributionsVolumeInvalid ||
                      duplicateDistributionId !== null ||
                      isSaving
                    }
                    onClick={handlePopupOpen}
                    sx={{ mt: '10px' }}
                  >
                    {isSaving ? (
                      <Box sx={{ alignItems: 'center', display: 'flex' }}>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Saving...
                      </Box>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                </>
              )}
            </Stack>
          </DialogActions>
        )}
        <DialogExplicitConfirmation
          confirmationMessage="If you still want to reject the shipment, enter the word below and click Confirm"
          confirmationValue="REJECT"
          dialogOpen={isRejectDialogOpen}
          handleCloseDialog={() => setIsRejectDialogOpen(false)}
          label="REJECT"
          onConfirm={handleRejectShipment}
          title="Are you sure you want to reject this shipment?"
          warningList={[
            'Once rejected, this shipment will be considered canceled.',
            'This action cannot be undone.',
          ]}
        />
        <DialogExplicitConfirmation
          confirmationMessage="If you still want to CANCEL the SHIPMENT, enter the word below and click Confirm."
          confirmationValue="CANCEL"
          dialogOpen={isExplicitDialogOpen}
          handleCloseDialog={() => {
            setIsExplicitDialogOpen(false);
            setTimeout(() => {
              setDialogType('');
            }, 600);
          }}
          label="CANCEL"
          onConfirm={handleConfirm}
          title="Are you sure you want to CANCEL this shipment?"
          warningList={warningList}
        />
        <MDialog
          header="Accept Shipment?"
          leftButtonAction={() => setIsAcceptDialogOpen(false)}
          leftButtonText="Back"
          onClose={() => {
            setIsAcceptDialogOpen(false);
          }}
          open={isAcceptDialogOpen}
          rightButtonAction={handleAcceptShipment}
          rightButtonText="Confirm"
          slotProps={{ paper: { sx: dialogContainerStyles } }}
        >
          <Typography>
            Are you sure you would like to accept shipment #{shipmentDataProp('shipmentId')}?
          </Typography>
        </MDialog>

        <Dialog
          maxWidth="xs"
          onClose={() => {
            setIsVolumeChangeDialogOpen(false);
          }}
          open={isVolumeChangeDialogOpen}
          slotProps={{
            paper: { elevation: 10 },
          }}
          sx={{ '& .MuiDialog-paper': { p: 1.2, width: 400 } }}
        >
          <DialogTitle>You are about to {volumeChangeMessage} the total Batch volume</DialogTitle>
          <DialogContent>
            <Typography sx={{ display: 'inline' }} variant="body1bold">
              Current volume
            </Typography>
            : {formatNumberWithoutDecimals(batchDataProp('volume') ?? 0)} bbl
            <br />
            <br />
            <Typography sx={{ display: 'inline' }} variant="body1bold">
              Volume after confirm
            </Typography>
            :{' '}
            {formatNumberWithoutDecimals(
              updatedBatchVolume(
                parseInt(shipmentUpdatesProp('volume')?.replace(/,/g, '') || 0),
                parseInt(shipmentDataProp('volume') || 0)
              ) ?? 0
            )}{' '}
            bbl
          </DialogContent>
          <Divider />
          <DialogContent sx={{ textAlign: 'center' }}>
            <Typography sx={{ display: 'inline', fontWeight: 'bold' }} variant="body2">
              If you still want to {volumeChangeMessage} the total batch volume, click confirm.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleVolumeReset} variant="outlined">
              Back
            </Button>
            <Button autoFocus onClick={handleVolumeConfirmChange}>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

ViewShipmentById.propTypes = {
  pageName: PropTypes.string,
};

ViewShipmentById.defaultProps = {
  pageName: '',
};
export default ViewShipmentById;
